import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import { uploadPdfCandidateFile } from "../../Redux/ActionCreators/candidateAction";
import { CANDIDATE_SOURCE } from "../../constants/candidateSource";
import { readDirectoryFiles } from "../Candidates/AddCandidatesModal";
import { FileTypeIcon, filterValidResumeFiles, MAX_FILES, AlreadyAppliedCheckbox } from "./sharedUI";
import useResumeUploadSocket, { isSuccessStatus, isFailedStatus } from "./useResumeUploadSocket";

const UploadResumesPanel = ({ jobId, onInvited }) => {
    const dispatch = useDispatch();
    const { uploadItems, registerItems } = useResumeUploadSocket();

    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [trackedIds, setTrackedIds] = useState([]);
    const [alreadyApplied, setAlreadyApplied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);
    const settledRef = useRef(false);
    // Snapshot of the checkbox taken when the batch is uploaded, so a later
    // toggle can't change how the in-flight batch is attached to the job.
    const alreadyAppliedRef = useRef(false);

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    // The job-scoped upload endpoint (uploadPdfCandidateFile →
    // /jobs/{jobId}/job-candidates/upload-resumes) attaches candidates to the
    // job itself and carries the already_applied flag, so once every tracked
    // resume has settled we only report progress + refresh the invite list.
    // Guarded so it fires once per upload batch.
    useEffect(() => {
        if (!trackedIds.length || settledRef.current) return;
        const items = trackedIds.map((id) => uploadItems[id]).filter(Boolean);
        if (items.length !== trackedIds.length) return;
        const allSettled = items.every((item) => isSuccessStatus(item.status) || isFailedStatus(item.status));
        if (!allSettled) return;

        settledRef.current = true;
        const successCount = items.filter((item) => isSuccessStatus(item.status)).length;
        const failedCount = items.length - successCount;
        const wasAlreadyApplied = alreadyAppliedRef.current;

        if (successCount > 0) {
            if (!wasAlreadyApplied) onInvited?.();
            const target = wasAlreadyApplied ? "added to the job" : "added to the invite list";
            setSnackbar({
                open: true,
                message: failedCount > 0
                    ? `${successCount} resume${successCount > 1 ? "s" : ""} ${target}, ${failedCount} failed.`
                    : `${successCount} resume${successCount > 1 ? "s" : ""} ${target}.`,
                severity: failedCount > 0 ? "warning" : "success",
            });
        } else {
            setSnackbar({ open: true, message: "All resumes failed to process. Please try again.", severity: "error" });
        }
        setTrackedIds([]);
    }, [uploadItems, trackedIds, onInvited]);

    const addFiles = useCallback((incoming) => {
        const valid = filterValidResumeFiles(incoming);
        setFiles((prev) => {
            const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
            const unique = valid.filter((f) => !existing.has(`${f.name}-${f.size}`));
            return [...prev, ...unique].slice(0, MAX_FILES);
        });
    }, []);

    const handleChooseFiles = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current += 1;
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
    };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

    const handleDrop = async (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        const items = Array.from(e.dataTransfer.items || []);
        const collected = [];
        for (const item of items) {
            if (item.kind !== "file") continue;
            const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
            if (entry?.isDirectory) {
                const nested = await readDirectoryFiles(entry);
                collected.push(...nested);
            } else {
                const file = item.getAsFile();
                if (file) collected.push(file);
            }
        }
        addFiles(collected);
    };

    const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const handleAddToInviteList = async () => {
        if (!files.length || uploading) return;
        setUploading(true);
        alreadyAppliedRef.current = alreadyApplied;
        try {
            const result = await dispatch(uploadPdfCandidateFile(jobId, files, alreadyApplied, CANDIDATE_SOURCE.RESUME_UPLOAD));
            const items = Array.isArray(result?.data) ? result.data : [];
            if (result?.success && items.length > 0) {
                registerItems(items);
                const ids = items.map((item) => item.resume_upload_id).filter(Boolean);
                settledRef.current = false;
                setTrackedIds(ids);
                setFiles([]);
                setSnackbar({ open: true, message: "Resumes uploaded. Processing in background…", severity: "success" });
            } else if (result?.success) {
                // Upload accepted but no per-file tracking records came back —
                // nothing to watch over the socket, so just refresh + report.
                if (!alreadyApplied) onInvited?.();
                setFiles([]);
                setSnackbar({
                    open: true,
                    message: alreadyApplied ? "Resumes uploaded. Candidates added to the job." : "Resumes uploaded. Invites are being sent.",
                    severity: "success",
                });
            } else {
                setSnackbar({ open: true, message: result?.message || "Upload failed", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.message || "Unexpected error occurred", severity: "error" });
        } finally {
            setUploading(false);
        }
    };

    const isProcessing = trackedIds.length > 0;

    return (
        <div className="icv2-upload-panel">
            <div className="icv2-upload-panel-head">
                <div className="icv2-upload-panel-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M9 15h6M9 11h6" />
                    </svg>
                </div>
                <div>
                    <h4>Upload resumes in a batch</h4>
                    <p>Add PDF, DOC, or DOCX files. We&rsquo;ll prepare a clean invite record for each file.</p>
                </div>
                <button className="icv2-outline-btn" onClick={() => fileInputRef.current?.click()}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Choose file
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleChooseFiles}
                />
            </div>

            {files.length === 0 ? (
                <div
                    className={`icv2-dropzone${isDragging ? " icv2-dropzone--dragging" : ""}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#F75891" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 14.9A5 5 0 0 1 6 5.3a6 6 0 0 1 11.3-1A5 5 0 0 1 18 14.9" />
                        <path d="M12 12v9" />
                        <path d="m8 16 4-4 4 4" />
                    </svg>
                    <p className="icv2-dropzone-title">Drop resumes here</p>
                    <p className="icv2-dropzone-sub">PDF, DOC, or DOCX · multiple files supported</p>
                </div>
            ) : (
                <div className="icv2-file-preview">
                    <div className="icv2-file-preview-head">
                        <p>{files.length} file{files.length > 1 ? "s" : ""} selected</p>
                        <span>max {MAX_FILES}</span>
                    </div>
                    <div className="icv2-file-preview-list">
                        {files.map((f, idx) => (
                            <div key={idx} className="icv2-file-row">
                                <FileTypeIcon name={f.name} />
                                <div className="icv2-file-row-info">
                                    <p title={f.name}>{f.name}</p>
                                    <span>{(f.size / 1024).toFixed(1)} KB</span>
                                </div>
                                <button className="icv2-remove-x" onClick={() => removeFile(idx)} title="Remove">×</button>
                            </div>
                        ))}
                        {files.length < MAX_FILES && (
                            <button className="icv2-outline-btn icv2-outline-btn--block" onClick={() => fileInputRef.current?.click()}>
                                + Add more files
                            </button>
                        )}
                    </div>
                </div>
            )}

            {isProcessing && (
                <div className="icv2-processing-list">
                    {trackedIds.map((id) => {
                        const item = uploadItems[id];
                        if (!item) return null;
                        const success = isSuccessStatus(item.status);
                        const failed = isFailedStatus(item.status);
                        return (
                            <div key={id} className={`icv2-processing-row${success ? " success" : failed ? " failed" : ""}`}>
                                <FileTypeIcon name={item.file_name || "file.pdf"} />
                                <div className="icv2-file-row-info">
                                    <p title={item.file_name}>{item.file_name}</p>
                                    <span>{item.ui_message || item.status}</span>
                                </div>
                                {!success && !failed && (
                                    <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                                        <span className="visually-hidden">Processing…</span>
                                    </div>
                                )}
                                {success && <span className="icv2-processing-badge success">✓ Done</span>}
                                {failed && <span className="icv2-processing-badge failed">× Failed</span>}
                            </div>
                        );
                    })}
                </div>
            )}

            <AlreadyAppliedCheckbox
                checked={alreadyApplied}
                onChange={setAlreadyApplied}
                disabled={uploading || isProcessing}
            />

            <button
                className="icv2-primary-btn icv2-primary-btn--block"
                onClick={handleAddToInviteList}
                disabled={!files.length || uploading || isProcessing}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
                {uploading
                    ? "Uploading…"
                    : isProcessing
                        ? "Processing…"
                        : alreadyApplied
                            ? "Add resumes to job candidates"
                            : "Upload resumes & send invites"}
            </button>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default UploadResumesPanel;
